import nodeMarkUrl from "../../skin/brand/node-mark.jpg?url";
import { initTheme } from "../theme-init.js";
import { currentInstallEnvironment } from "./install-environment.js";
import { InstallLandingController } from "./install-landing-controller.js";
/**
 * Install-landing screen — the entry page shown before a Client Node PWA has been
 * installed: an install pitch (native prompt on Chromium/Android, manual add-to-home-screen
 * instructions on iOS Safari, manual add-to-Dock instructions on macOS Safari, a
 * browser-only fallback everywhere else), an "already installed" state, and the post-install
 * success state. Drives an in-process
 * `InstallLandingController` against the injected `InstallEnvironmentPort`, exactly the way
 * `../onboarding/onboarding-gate.ts` drives `OnboardingController` — this element owns no
 * install-detection state of its own, it only renders whatever view the controller derives.
 *
 * Mirrors `../onboarding/onboarding-chrome.ts`'s idiom: `extends HTMLElement`, shadow root +
 * inline `<style>` using `var(--token, <light-literal-fallback>)` so the shell's token sheet
 * (adopted at the `<node-app-shell>` root, or the document-level global sheet when this
 * screen is mounted standalone before the shell exists — see `shell/theme-init.ts`'s "two
 * real elements" note) drives theming while still standing alone if this element is ever
 * mounted outside that tree, plus the same reduced-motion/forced-colors guards. It imports
 * the SAME brand mark as `onboarding-chrome.ts`.
 *
 * `installEnvironment` mirrors `../pairing/pairing-gate.ts`'s `set client`: re-initializing
 * the controller whenever it's assigned while connected, so setting it either before or
 * after the element is inserted into the document both work. Left unset, it falls back to
 * the composition-root-populated singleton (`./install-environment.ts`'s
 * `currentInstallEnvironment()`), the same fallback pattern `shell/theme-init.ts` uses for
 * `ThemeEnvironmentPort`.
 */
export const INSTALL_LANDING_TAG = "node-install-landing";
const SVG_NAMESPACE = "http://www.w3.org/2000/svg";
/** iOS "Share" glyph, per the design's inline SVG spec — built via `createElementNS` (like
 * `../pairing/qr-code.ts`'s `renderQrCode`) rather than `innerHTML`, matching the DOM-only
 * construction the rest of this element's markup uses. */
function createShareIcon() {
    const svg = document.createElementNS(SVG_NAMESPACE, "svg");
    svg.setAttribute("width", "18");
    svg.setAttribute("height", "22");
    svg.setAttribute("viewBox", "0 0 18 22");
    svg.setAttribute("fill", "none");
    svg.setAttribute("aria-hidden", "true");
    svg.append(svgPath("M9 1.5 V13", { "stroke-linecap": "round" }), svgPath("M5 5 L9 1.5 L13 5", { "stroke-linecap": "round", "stroke-linejoin": "round" }), svgPath("M4 8.5 H2.5 V20.5 H15.5 V8.5 H14", { "stroke-linecap": "round", "stroke-linejoin": "round" }));
    return svg;
}
/** Success/"Installed" check glyph — same DOM-construction rationale as `createShareIcon`. */
function createCheckIcon() {
    const svg = document.createElementNS(SVG_NAMESPACE, "svg");
    svg.setAttribute("width", "14");
    svg.setAttribute("height", "14");
    svg.setAttribute("viewBox", "0 0 14 14");
    svg.setAttribute("fill", "none");
    svg.setAttribute("aria-hidden", "true");
    svg.append(svgPath("M2.5 7.3 L5.6 10.4 L11.5 3.8", { "stroke-width": "1.8", "stroke-linecap": "round", "stroke-linejoin": "round" }));
    return svg;
}
function svgPath(d, extra) {
    const path = document.createElementNS(SVG_NAMESPACE, "path");
    path.setAttribute("d", d);
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", extra["stroke-width"] ?? "1.6");
    for (const [name, value] of Object.entries(extra)) {
        if (name === "stroke-width")
            continue;
        path.setAttribute(name, value);
    }
    return path;
}
const HERO_STEPS = [
    { index: "01", text: "Works offline once installed" },
    { index: "02", text: "Pairs with your node over your local network" },
    { index: "03", text: "No account, no cloud — your keys stay home" },
];
export class NodeInstallLandingElementImpl extends HTMLElement {
    #installEnvironment;
    #onContinue = () => undefined;
    #controller;
    #view = "unsupported";
    /** Dark-mode class subscription (`../theme-init.js`), mirroring `app-shell.ts`'s own
     * `#themeHandle` idiom exactly — this element is mounted directly in `#app`, outside
     * `<node-app-shell>`, so nothing else runs the theme resolver for it. Applied in
     * `connectedCallback`, disposed in `disconnectedCallback`; guarded so connect/disconnect
     * churn never leaks a duplicate `matchMedia` listener. */
    #themeHandle;
    set installEnvironment(value) {
        this.#installEnvironment = value;
        if (this.isConnected)
            this.#start();
    }
    get installEnvironment() {
        return this.#installEnvironment ?? currentInstallEnvironment();
    }
    set onContinue(value) {
        this.#onContinue = value;
    }
    get onContinue() {
        return this.#onContinue;
    }
    connectedCallback() {
        this.#themeHandle?.dispose();
        this.#themeHandle = initTheme(this);
        this.#start();
    }
    disconnectedCallback() {
        this.#themeHandle?.dispose();
        this.#themeHandle = undefined;
        this.#controller?.stop();
        this.#controller = undefined;
    }
    #start() {
        this.#controller?.stop();
        const controller = new InstallLandingController(this.installEnvironment, (view) => {
            this.#view = view;
            this.#render();
        });
        this.#controller = controller;
        controller.start();
    }
    #render() {
        const root = this.shadowRoot ?? this.attachShadow({ mode: "open" });
        root.replaceChildren();
        const style = document.createElement("style");
        style.textContent = STYLE;
        const container = document.createElement("div");
        container.className = "landing";
        switch (this.#view) {
            case "installable":
                container.append(this.#renderHero(), this.#renderInstallable());
                break;
            case "ios":
                container.append(this.#renderHero(), this.#renderIos());
                break;
            case "safari-desktop":
                container.append(this.#renderHero(), this.#renderSafariDesktop());
                break;
            case "unsupported":
                container.append(this.#renderHero(), this.#renderUnsupported());
                break;
            case "installed":
                container.append(this.#renderInstalled());
                break;
            case "success":
                container.append(this.#renderSuccess());
                break;
        }
        root.append(style, container);
    }
    #renderLogo(className = "logo") {
        const logo = document.createElement("img");
        logo.className = className;
        logo.src = nodeMarkUrl;
        logo.alt = "Node";
        return logo;
    }
    #renderHero() {
        const hero = document.createElement("div");
        hero.className = "hero";
        const eyebrow = document.createElement("p");
        eyebrow.className = "eyebrow";
        eyebrow.textContent = "Client Node";
        const heading = document.createElement("h1");
        const em = document.createElement("em");
        em.textContent = "on this device";
        heading.append("Your node, ", em);
        const body = document.createElement("p");
        body.className = "body";
        body.textContent =
            "Client Node pairs with your own Lightning node over your local network — installed like an app, running in your browser.";
        const card = document.createElement("div");
        card.className = "hero-card";
        const list = document.createElement("ul");
        for (const step of HERO_STEPS) {
            const item = document.createElement("li");
            const index = document.createElement("span");
            index.className = "step-index";
            index.textContent = step.index;
            const text = document.createElement("span");
            text.textContent = step.text;
            item.append(index, text);
            list.append(item);
        }
        card.append(list);
        hero.append(this.#renderLogo(), eyebrow, heading, body, card);
        return hero;
    }
    #renderInstallable() {
        const actions = document.createElement("div");
        actions.className = "actions";
        const install = document.createElement("button");
        install.type = "button";
        install.className = "primary";
        install.setAttribute("data-anchor", "install");
        install.textContent = "Install Client Node";
        install.addEventListener("click", () => {
            void this.#controller?.install();
        });
        const continueLink = this.#continueLink("Continue in browser");
        actions.append(install, continueLink);
        return actions;
    }
    #renderIos() {
        const wrapper = document.createElement("div");
        wrapper.className = "actions";
        const card = document.createElement("div");
        card.className = "ios-card";
        const heading = document.createElement("h2");
        heading.className = "ios-eyebrow";
        heading.textContent = "Add to Home Screen";
        const steps = document.createElement("ol");
        const stepLabels = [
            () => {
                const label = document.createElement("span");
                label.className = "step-label";
                label.append("Tap the Share icon ", createShareIcon(), " in the toolbar");
                return label;
            },
            () => {
                const label = document.createElement("span");
                label.className = "step-label";
                label.textContent = 'Choose "Add to Home Screen"';
                return label;
            },
            () => {
                const label = document.createElement("span");
                label.className = "step-label";
                label.textContent = "Open Client Node from your home screen";
                return label;
            },
        ];
        stepLabels.forEach((buildLabel, position) => {
            const item = document.createElement("li");
            const chip = document.createElement("span");
            chip.className = "step-chip";
            chip.textContent = String(position + 1);
            item.append(chip, buildLabel());
            steps.append(item);
        });
        card.append(heading, steps);
        wrapper.append(card, this.#continueLink("Continue in browser"));
        return wrapper;
    }
    /** macOS Safari 17+'s desktop analog of `#renderIos`'s "Add to Home Screen" card — Safari
     * never fires `beforeinstallprompt`, so this is the only path a real Mac gets to install,
     * via Share → Add to Dock. Same card/steps structure and styling as `#renderIos` (reuses
     * the `.ios-card`/`.ios-eyebrow` styles rather than duplicating them). */
    #renderSafariDesktop() {
        const wrapper = document.createElement("div");
        wrapper.className = "actions";
        const card = document.createElement("div");
        card.className = "ios-card";
        const heading = document.createElement("h2");
        heading.className = "ios-eyebrow";
        heading.textContent = "Add to Dock";
        const steps = document.createElement("ol");
        const stepLabels = [
            () => {
                const label = document.createElement("span");
                label.className = "step-label";
                label.append("Click the Share button ", createShareIcon(), " in the toolbar");
                return label;
            },
            () => {
                const label = document.createElement("span");
                label.className = "step-label";
                label.textContent = 'Choose "Add to Dock"';
                return label;
            },
            () => {
                const label = document.createElement("span");
                label.className = "step-label";
                label.textContent = "Open Client Node from your Dock";
                return label;
            },
        ];
        stepLabels.forEach((buildLabel, position) => {
            const item = document.createElement("li");
            const chip = document.createElement("span");
            chip.className = "step-chip";
            chip.textContent = String(position + 1);
            item.append(chip, buildLabel());
            steps.append(item);
        });
        card.append(heading, steps);
        wrapper.append(card, this.#continueLink("Continue in browser"));
        return wrapper;
    }
    #renderUnsupported() {
        const wrapper = document.createElement("div");
        wrapper.className = "actions";
        const body = document.createElement("p");
        body.className = "body";
        body.textContent = "Your browser may not support installing apps — you can still use Client Node in this tab.";
        const continueLink = document.createElement("a");
        continueLink.href = "#";
        continueLink.className = "primary primary-link";
        continueLink.setAttribute("data-anchor", "continue");
        continueLink.textContent = "Continue in browser";
        continueLink.addEventListener("click", (event) => {
            event.preventDefault();
            this.#onContinue();
        });
        wrapper.append(body, continueLink);
        return wrapper;
    }
    #renderInstalled() {
        const wrapper = document.createElement("div");
        wrapper.className = "actions installed";
        const statusRow = document.createElement("div");
        statusRow.className = "status-row";
        statusRow.append(createCheckIcon());
        const statusLabel = document.createElement("span");
        statusLabel.textContent = "Installed";
        statusRow.append(statusLabel);
        const heading = document.createElement("h1");
        heading.className = "installed-heading";
        heading.textContent = "Client Node is installed on this device";
        const open = document.createElement("button");
        open.type = "button";
        open.className = "primary";
        open.setAttribute("data-anchor", "open");
        open.textContent = "Open Client Node";
        open.addEventListener("click", () => this.#onContinue());
        const continueLink = this.#continueLink("or continue in browser");
        wrapper.append(this.#renderLogo("logo logo-small"), statusRow, heading, open, continueLink);
        return wrapper;
    }
    #renderSuccess() {
        const wrapper = document.createElement("div");
        wrapper.className = "success";
        const circle = document.createElement("div");
        circle.className = "success-circle";
        circle.append(createCheckIcon());
        const eyebrow = document.createElement("p");
        eyebrow.className = "eyebrow success-eyebrow";
        eyebrow.textContent = "Installed";
        const heading = document.createElement("h1");
        const em = document.createElement("em");
        em.textContent = "home screen";
        heading.append("Launch Client Node from your ", em);
        const body = document.createElement("p");
        body.className = "body";
        body.textContent = "You can close this tab — Client Node now lives on your device.";
        wrapper.append(circle, eyebrow, heading, body);
        return wrapper;
    }
    #continueLink(text) {
        const link = document.createElement("a");
        link.href = "#";
        link.className = "continue-link";
        link.setAttribute("data-anchor", "continue");
        link.textContent = text;
        link.addEventListener("click", (event) => {
            event.preventDefault();
            this.#onContinue();
        });
        return link;
    }
}
const STYLE = `
  :host { display: block; font-family: var(--ui-font, Inter, system-ui, sans-serif); color: var(--ink-hi, #0A0A0A); }
  .landing {
    display: flex; flex-direction: column; align-items: center; text-align: center;
    max-width: 440px; margin: 0 auto; padding: clamp(40px, 10vw, 72px) clamp(20px, 6vw, 32px);
    gap: 24px;
  }
  .hero { display: flex; flex-direction: column; align-items: center; gap: 16px; }
  .logo { display: block; height: 56px; width: auto; object-fit: contain; }
  .logo-small { height: 32px; }
  :host(.dark) .logo { filter: invert(1); }
  .eyebrow {
    margin: 0; font: 600 10.5px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
    letter-spacing: 0.22em; text-transform: uppercase; color: var(--c-coral, #DF6662);
  }
  .ios-eyebrow {
    margin: 0 0 4px; font: 600 10.5px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
    letter-spacing: 0.22em; text-transform: uppercase; color: var(--c-coral, #DF6662);
  }
  h1 {
    margin: 0; font: 400 clamp(1.75rem, 7vw, 2.5rem)/1.1 var(--ui-serif, "Instrument Serif", serif);
  }
  h1 em { font-style: italic; color: var(--c-coral, #DF6662); }
  .body { margin: 0; max-width: 340px; font-size: 14px; line-height: 1.6; color: var(--ink-mid, #545454); }
  .hero-card {
    width: 100%; box-sizing: border-box; text-align: left;
    background: var(--bg-1, #E9E6DE); border: 1px solid var(--hr, rgba(10, 10, 10, 0.07));
    border-radius: var(--ui-radius-lg, 14px); padding: 18px 20px;
  }
  .hero-card ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }
  .hero-card li { display: flex; align-items: baseline; gap: 12px; font-size: 13px; color: var(--ink-hi, #0A0A0A); }
  .step-index {
    font: 600 11px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
    color: var(--c-green, #36AA75); flex-shrink: 0;
  }
  .actions {
    display: flex; flex-direction: column; align-items: center; gap: 14px; width: 100%;
  }
  .actions.installed { gap: 12px; }
  .primary {
    display: block; width: 100%; box-sizing: border-box; min-height: 2.75rem;
    border: none; border-radius: var(--ui-radius, 10px);
    padding: .75rem 1.25rem; font: 600 .9375rem/1.2 var(--ui-font, Inter, system-ui, sans-serif);
    color: var(--c-white, #FFFFFF); background: var(--c-coral, #DF6662); cursor: pointer;
    transition: background 0.18s var(--ease-out, ease-out);
    text-decoration: none; text-align: center;
  }
  .primary:hover { background: var(--c-coral-deep, #B85551); }
  .primary-link { display: block; }
  .continue-link {
    background: none; border: none; padding: 0; cursor: pointer;
    font: 400 13px/1.4 var(--ui-font, Inter, system-ui, sans-serif);
    color: var(--ink-low, #888888); text-decoration: underline;
  }
  .primary:focus-visible, .continue-link:focus-visible {
    outline: none; box-shadow: 0 0 0 3px rgba(223, 102, 98, 0.45); border-radius: var(--ui-radius, 10px);
  }
  .ios-card {
    width: 100%; box-sizing: border-box; text-align: left;
    background: var(--bg-raised, #FFFFFF); border: 1px solid var(--hr, rgba(10, 10, 10, 0.07));
    border-radius: var(--ui-radius-lg, 14px); padding: 20px 22px;
  }
  .ios-card ol { list-style: none; margin: 16px 0 0; padding: 0; display: flex; flex-direction: column; gap: 14px; }
  .ios-card li { display: flex; align-items: flex-start; gap: 12px; }
  .step-chip {
    flex-shrink: 0; display: flex; align-items: center; justify-content: center;
    width: 22px; height: 22px; border-radius: 999px;
    background: var(--bg-1, #E9E6DE); border: 1px solid var(--hr-strong, rgba(10, 10, 10, 0.12));
    font: 600 11px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
    color: var(--ink-hi, #0A0A0A);
  }
  .step-label { font-size: 13.5px; line-height: 1.5; color: var(--ink-hi, #0A0A0A); display: flex; align-items: center; gap: 6px; }
  .step-label svg { flex-shrink: 0; color: var(--ink-mid, #545454); vertical-align: middle; }
  .status-row {
    display: flex; align-items: center; gap: 8px;
    font: 600 11px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
    letter-spacing: 0.1em; text-transform: uppercase;
    color: light-dark(var(--c-green-deep, #2B7258), var(--c-green, #36AA75));
  }
  .status-row svg { color: var(--c-green, #36AA75); }
  .installed-heading { font: 400 clamp(1.5rem, 6vw, 1.875rem)/1.15 var(--ui-serif, "Instrument Serif", serif); }
  .success { display: flex; flex-direction: column; align-items: center; gap: 16px; }
  .success-circle {
    display: flex; align-items: center; justify-content: center;
    width: 56px; height: 56px; border-radius: 999px;
    background: rgba(54, 170, 117, 0.14); border: 1px solid var(--c-green, #36AA75);
    color: var(--c-green, #36AA75);
    animation: node-install-success-pop 0.4s var(--ease-out, ease-out);
  }
  @keyframes node-install-success-pop {
    0% { transform: scale(0.7); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }
  .success-eyebrow { color: var(--c-green, #36AA75); }
  @media (prefers-reduced-motion: reduce) {
    * { scroll-behavior: auto !important; transition-duration: 0.01ms !important; animation: none !important; }
  }
  @media (forced-colors: active) {
    .hero-card, .ios-card, .step-chip, .success-circle { border: 1px solid CanvasText; }
    .primary { border: 1px solid CanvasText; forced-color-adjust: none; }
  }
`;
//# sourceMappingURL=install-landing.js.map